import React, { useCallback, useEffect, useRef, useState } from "react";
import DemoButton from "../shared/DemoButton";
import { SOLUTIONS_HERO_ITEMS } from "./solutionsData";

const MOBILE_CAROUSEL_QUERY = "(max-width: 1023px)";

const CAROUSEL_METRICS = {
  desktop: { itemStep: 56, viewportHeight: 392, rotateMs: 3000 },
  mobile: { itemStep: 88, viewportHeight: 616, rotateMs: 3000 },
};

const ITEM_COUNT = SOLUTIONS_HERO_ITEMS.length;
const START_POSITION = ITEM_COUNT + 3;

const EXTENDED_ITEMS = [
  ...SOLUTIONS_HERO_ITEMS,
  ...SOLUTIONS_HERO_ITEMS,
  ...SOLUTIONS_HERO_ITEMS,
];

function useCarouselMetrics() {
  const [metrics, setMetrics] = useState(CAROUSEL_METRICS.desktop);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_CAROUSEL_QUERY);

    const applyMetrics = () => {
      setMetrics(mediaQuery.matches ? CAROUSEL_METRICS.mobile : CAROUSEL_METRICS.desktop);
    };

    applyMetrics();
    mediaQuery.addEventListener("change", applyMetrics);
    return () => mediaQuery.removeEventListener("change", applyMetrics);
  }, []);

  return metrics;
}

function getTrackOffset(position, viewportHeight, itemStep) {
  return viewportHeight / 2 - (position * itemStep + itemStep / 2);
}

function getOpacityFromCenter(itemIndex, trackOffset, viewportHeight, itemStep) {
  const itemCenter = itemIndex * itemStep + itemStep / 2 + trackOffset;
  const distanceSlots = Math.abs(itemCenter - viewportHeight / 2) / itemStep;

  if (distanceSlots <= 0.35) return 1;
  if (distanceSlots <= 1.35) return 0.42;
  if (distanceSlots <= 2.35) return 0.24;
  return 0.14;
}

export default function SolutionsHero() {
  const { itemStep, viewportHeight, rotateMs } = useCarouselMetrics();
  const [position, setPosition] = useState(START_POSITION);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const trackRef = useRef(null);

  const trackOffset = getTrackOffset(position, viewportHeight, itemStep);

  const normalizePosition = useCallback(() => {
    setTransitionEnabled(false);
    setPosition((current) => {
      if (current >= ITEM_COUNT * 2) return current - ITEM_COUNT;
      if (current < ITEM_COUNT) return current + ITEM_COUNT;
      return current;
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setTransitionEnabled(true));
    });
  }, []);

  const handleTransitionEnd = (event) => {
    if (event.propertyName !== "transform" || event.target !== trackRef.current) return;
    if (position >= ITEM_COUNT * 2 || position < ITEM_COUNT) {
      normalizePosition();
    }
  };

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return undefined;

    const intervalId = window.setInterval(() => {
      setPosition((current) => current + 1);
    }, rotateMs);

    return () => window.clearInterval(intervalId);
  }, [rotateMs]);

  const handleItemClick = (index) => {
    setTransitionEnabled(true);
    setPosition(ITEM_COUNT + index);
  };

  return (
    <section className="sol-hero">
      <div className="sol-hero__inner">
        <div className="sol-hero__copy">
          <div className="sol-hero__badge">
            <span className="sol-hero__badge-dot" />
            SOLUTIONS
          </div>
          <h1>
            One platform. Every
            <br />
            operational <span className="sol-hero__accent-word">blind</span>
            <br />
            <span className="sol-hero__accent-line">spot covered.</span>
          </h1>
          <p className="sol-hero__sub">
            Insurance and Intelligence run on the same explainable layer — here's exactly what
            that solves, day to day.
          </p>
          <div className="sol-hero__actions">
            <DemoButton />
            <a className="sol-btn sol-btn--ghost" href="#sol-blocks">
              Learn How It Works →
            </a>
          </div>
        </div>

        <div className="sol-hero__carousel" aria-label="Solution highlights">
          <div
            className="sol-hero__carousel-viewport"
            style={{ height: `${viewportHeight}px` }}
          >
            <span className="sol-hero__carousel-arrow" aria-hidden="true">
              ▶
            </span>
            <ul
              ref={trackRef}
              className={`sol-hero__carousel-track${transitionEnabled ? "" : " is-instant"}`}
              style={{ transform: `translateY(${trackOffset}px)` }}
              onTransitionEnd={handleTransitionEnd}
            >
              {EXTENDED_ITEMS.map((item, index) => {
                const isActive = index === position;
                const opacity = getOpacityFromCenter(index, trackOffset, viewportHeight, itemStep);

                return (
                  <li key={`${item}-${index}`} style={{ height: `${itemStep}px` }}>
                    <button
                      type="button"
                      className={`sol-hero__carousel-item${isActive ? " is-active" : ""}`}
                      style={{ opacity, minHeight: `${itemStep}px` }}
                      onClick={() => handleItemClick(index % ITEM_COUNT)}
                      aria-current={isActive ? "true" : undefined}
                      tabIndex={isActive ? 0 : -1}
                    >
                      {item}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <p className="sol-hero__fine">
        No credit card required · Built for SOC 2 / GDPR-ready environments
      </p>
    </section>
  );
}
